
import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { FiX, FiUploadCloud, FiCheck, FiArrowRight, FiAlertTriangle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import Button from '../Button';
import Select from '../Select';
import Api from '../../Api';
import './style.css';

const DB_FIELDS = [
  { value: 'name', label: 'Nome / Razão Social' },
  { value: 'company_id', label: 'ID da Empresa' },
  { value: 'nickname', label: 'Apelido / Lotação' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'address_zip_code', label: 'CEP' },
  { value: 'address_street', label: 'Endereço (Rua/Av)' },
  { value: 'address_number', label: 'Número' },
  { value: 'address_complement', label: 'Complemento' },
  { value: 'address_district', label: 'Bairro' },
  { value: 'address_city', label: 'Cidade' },
  { value: 'address_state', label: 'Estado (UF)' },
  { value: 'fence_radius_km', label: 'Raio da Cerca (Km)' },
  { value: 'responsible_email', label: 'E-mail' },
  { value: 'status', label: 'Status' },
  { value: 'rotativo', label: 'Rotativo (Sim/Não)' },
  // Add other fields if necessary
];

export default function StationImportModal({ isOpen, onClose, onSuccess, companyId, companies = [] }) {
  const [step, setStep] = useState('upload'); // upload, map, review, processing
  const [fileData, setFileData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({}); // { headerName: dbField }
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(companyId || '');

  const normalizeString = (str) => {
    if (!str) return '';
    return String(str).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  };

  const findCompany = (companiesList, ref) => {
    if (!ref) return null;
    const normalizedRef = normalizeString(ref);
    const exactId = companiesList.find(c => String(c.id) === String(ref));
    if (exactId) return exactId;
    return companiesList.find(c => {
      const name1 = normalizeString(c.corporate_name);
      const name2 = normalizeString(c.trade_name);
      return (name1 && (name1.includes(normalizedRef) || normalizedRef.includes(name1))) ||
             (name2 && (name2.includes(normalizedRef) || normalizedRef.includes(name2)));
    }) || null;
  };

  useEffect(() => {
    setSelectedCompanyId(companyId || '');
  }, [companyId]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 }); // Array of arrays

        if (data.length < 2) {
          toast.warning('O arquivo parece estar vazio ou sem dados.');
          return;
        }

        const fileHeaders = data[0];
        const rows = data.slice(1);

        setHeaders(fileHeaders);
        setFileData(rows);
        
        // Auto-map based on similar names
        const initialMapping = {};
        fileHeaders.forEach(header => {
          const lowerHeader = header.toLowerCase();
          if (lowerHeader.includes('cnpj')) initialMapping[header] = 'cnpj';
          else if (lowerHeader.includes('empresa') || lowerHeader.includes('company')) initialMapping[header] = 'company_ref';
          else if (lowerHeader.includes('razao') || lowerHeader.includes('razão') || lowerHeader.includes('nome')) initialMapping[header] = 'name';
          else if (lowerHeader.includes('apelido')) initialMapping[header] = 'nickname';
          else if (lowerHeader.includes('cep')) initialMapping[header] = 'address_zip_code';
          else if (lowerHeader.includes('bairro')) initialMapping[header] = 'address_district';
          else if (lowerHeader.includes('cidade')) initialMapping[header] = 'address_city';
          else if (lowerHeader.includes('uf') || lowerHeader.includes('estado')) initialMapping[header] = 'address_state';
          else if (lowerHeader.includes('numero') || lowerHeader.includes('número')) initialMapping[header] = 'address_number';
          else if (lowerHeader.includes('complemento')) initialMapping[header] = 'address_complement';
          else if (lowerHeader.includes('endereço') || lowerHeader.includes('endereco')) initialMapping[header] = 'address_street';
          else if (lowerHeader.includes('cerca') || lowerHeader.includes('km')) initialMapping[header] = 'fence_radius_km';
          else if (lowerHeader.includes('email') || lowerHeader.includes('e-mail')) initialMapping[header] = 'responsible_email';
          else if (lowerHeader.includes('status')) initialMapping[header] = 'status';
        });
        
        setMapping(initialMapping);
        setStep('map');
      } catch (error) {
        console.error(error);
        toast.error('Erro ao ler o arquivo.');
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleMappingChange = (header, dbField) => {
    setMapping(prev => ({ ...prev, [header]: dbField }));
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      
      // Transform data based on mapping
      const stationsToCreate = fileData.map(row => {
        const station = { company_id: selectedCompanyId };
        
        headers.forEach((header, index) => {
          const dbField = mapping[header];
          if (dbField) {
            let value = row[index];
            
            if (dbField === 'company_ref') {
                const matchedCompany = findCompany(companies, value);
                if (matchedCompany) {
                  station.company_id = matchedCompany.id;
                } else if (!isNaN(Number(value))) {
                  station.company_id = Number(value);
                }
                return;
            }

            // Data cleaning/formatting
            if (dbField === 'fence_radius_km' && typeof value === 'string') {
                value = parseFloat(value.replace('km', '').replace(',', '.').trim()) || 0;
            }
            if (dbField === 'cnpj' && value) {
                value = String(value).replace(/\D/g, ''); 
            }

            station[dbField] = value;
          }
        });

        // If 'company' field was mapped, we might need to resolve it.
        if (!station.company_id && selectedCompanyId) {
             station.company_id = selectedCompanyId;
        }

        // Defaults
        if (!station.type) station.type = 'private';
        if (!station.status) station.status = 'ativo';
        if (!station.rotativo) station.rotativo = 'nao'; // Default
        
        return station;
      }).filter(s => s.name && s.company_id); // Ensure name and company exists

      if (stationsToCreate.length === 0) {
        toast.warning('Nenhum dado válido para importar (verifique se Nome e Empresa estão definidos).');
        setLoading(false);
        return;
      }

      // Group by company to batch import? Or just send all?
      // Backend likely expects single companyId or array with company_ids.
      // Current backend signature: importServiceStations(companyId, data)
      // If companyId is null/all, we might need to iterate or update backend.
      // Let's assume we use the first companyId if passed, or we need to handle mixed.
      
      // If we are in "All companies" mode (companyId is null/all), we might need to split by company.
      // However, Api.importServiceStations takes a companyId URL param usually.
      // Let's check Api.js.

      // If stations have different company_ids, we need to handle that.
      // Simple approach: Group by company_id and call API for each.
      
      const stationsByCompany = stationsToCreate.reduce((acc, station) => {
        const cId = station.company_id;
        if (!acc[cId]) acc[cId] = [];
        acc[cId].push(station);
        return acc;
      }, {});

      for (const cId of Object.keys(stationsByCompany)) {
          await Api.importServiceStations(cId, stationsByCompany[cId]);
      }
      
      toast.success(`${stationsToCreate.length} postos importados com sucesso!`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao importar dados. Verifique o console.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('upload');
    setFileData([]);
    setHeaders([]);
    setMapping({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { reset(); onClose(); }}
      title="Importar Postos de Serviço"
      size="lg"
    >
      <div className="import-modal-body">
        <div className="import-content-area">
            {step === 'upload' && (
              
                <div className="upload-step-container">
                    {companies.length > 0 && (
                       <div className="company-select-container" style={{ marginBottom: '1rem' }}>
                           <Select
                               label="Selecione a empresa padrão para importação"
                               options={[{ value: '', label: 'Detectar do arquivo (coluna ID da Empresa) ou Selecione...' }, ...companies.map(c => ({ value: c.id, label: c.corporate_name || c.trade_name }))]}
                               value={selectedCompanyId}
                               onChange={e => setSelectedCompanyId(e.target.value)}
                               useCustomDropdown={true}
                               searchable
                           />
                       </div>
                    )}
                    <div 
                        className="upload-area"
                        onClick={() => {
                            fileInputRef.current.click();
                        }}
                    >
                    <FiUploadCloud className="upload-icon" />
                    <p className="upload-text">
                        Clique para selecionar um arquivo Excel (.xlsx) ou CSV
                    </p>
                    <p className="upload-subtext">O arquivo deve conter cabeçalhos na primeira linha.</p>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept=".xlsx, .xls, .csv"
                        style={{ display: 'none' }}
                    />
                </div>
              </div>
            )}

            {step === 'map' && (
                <div className="map-step">
                    <p className="step-description">Mapeie as colunas do seu arquivo para os campos do sistema.</p>
                    
                    <div className="mapping-grid">
                        {headers.map((header, idx) => (
                            <div key={idx} className="mapping-row">
                                <div className="file-header">
                                    <span className="label">Coluna do Arquivo:</span>
                                    <strong>{header}</strong>
                                    {fileData[0] && (
                                        <span className="example-value">Ex: {fileData[0][idx]}</span>
                                    )}
                                </div>
                                <div className="arrow-icon">
                                    <FiArrowRight />
                                </div>
                                <div className="db-field-select">
                                    <Select
                                        options={[{ value: '', label: 'Ignorar' }, ...DB_FIELDS]}
                                        value={mapping[header] || ''}
                                        onChange={(e) => handleMappingChange(header, e.target.value)}
                                        placeholder="Selecione o campo..."
                                        useCustomDropdown={true}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>

        <div className="modal-actions">
            <Button 
                onClick={() => step === 'upload' ? onClose() : reset()} 
                className="cancel-button"
                disabled={loading}
            >
                {step === 'upload' ? 'Cancelar' : 'Voltar'}
            </Button>
            
            {step === 'map' && (
                <Button onClick={handleImport} disabled={loading}>
                    {loading ? 'Importando...' : 'Importar Dados'}
                </Button>
            )}
        </div>
      </div>
    </Modal>
  );
}
